import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { api } from '@/utils/api';

type Route = 'stock' | 'buy' | 'make' | 'service' | 'blocked' | 'not_routed';
interface LineView { salesOrderItemId: string; description: string; orderedQty: number; dispatchedQty: number; route: Route; fromLabel: string; statusLabel: string; blockReason: string | null }
interface PoolView { id: string; description: string; qty: number; dispatchRef: string; shortCode: string; reason: string }
interface LinesView { salesOrderId: string; number: string; routed: boolean; blocked: boolean; banner: string | null; atpSource: 'stub' | 'live'; lines: LineView[]; pendingPool: PoolView[] }

// Same colour pairs as STATUS_BADGE on SalesOrderFulfilmentPage.
const ROUTE_PILL: Record<Route, { label: string; cls: string }> = {
  stock: { label: 'stock', cls: 'bg-green-50 text-green-700' },
  buy: { label: 'buy', cls: 'bg-blue-50 text-blue-700' },
  make: { label: 'make', cls: 'bg-yellow-50 text-yellow-700' },
  service: { label: 'service', cls: 'bg-gray-100 text-gray-600' },
  blocked: { label: 'no product', cls: 'bg-red-50 text-red-700' },
  not_routed: { label: 'not routed', cls: 'bg-gray-100 text-gray-600' },
};

export function FulfilmentLinesPanel({ soId, onRouted }: { soId: string; onRouted?: () => void }) {
  const [view, setView] = useState<LinesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [routing, setRouting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(`/so-fulfilment/${soId}/lines`);
      setView(res.data?.data ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load fulfilment lines');
    } finally {
      setLoading(false);
    }
  }, [soId]);

  useEffect(() => { load(); }, [load]);

  const reroute = async () => {
    setRouting(true);
    try {
      setError(null);
      await api.post(`/so-fulfilment/${soId}/route`, {});
      await load();
      onRouted?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to route the order');
    } finally {
      setRouting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div data-testid="fulfilment-lines-panel" className="mb-6 overflow-hidden rounded-xl border" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
      <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: 'var(--border-primary)' }}>
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Lines</p>
        {view && (view.blocked || !view.routed) && (
          <button
            onClick={reroute}
            disabled={routing}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-60"
          >
            {routing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {view.routed ? 'Check again' : 'Route now'}
          </button>
        )}
      </div>

      {error && <p className="border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-800">{error}</p>}

      {view?.banner && (
        <div className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{view.banner}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {['Line', 'Qty', 'Route', 'From', 'Status', 'Dispatched'].map((h) => (
                <th key={h} className={`px-4 py-3 font-medium ${h === 'Qty' || h === 'Dispatched' ? 'text-right' : 'text-left'}`} style={{ color: 'var(--text-secondary)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {(view?.lines ?? []).map((l) => (
              <tr key={l.salesOrderItemId} title={l.blockReason ?? undefined}>
                <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{l.description}</td>
                <td className="px-4 py-3 text-right" style={{ color: 'var(--text-primary)' }}>{l.orderedQty}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ROUTE_PILL[l.route].cls}`}>{ROUTE_PILL[l.route].label}</span>
                </td>
                <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{l.fromLabel}</td>
                <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{l.statusLabel}</td>
                <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>{l.route === 'blocked' ? '—' : `${l.dispatchedQty} / ${l.orderedQty}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {view && view.pendingPool.length > 0 && (
        <div className="border-t px-5 py-3" style={{ borderColor: 'var(--border-primary)' }}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Pending pool</p>
          {view.pendingPool.map((p) => (
            <p key={p.id} className="text-sm" style={{ color: 'var(--text-primary)' }}>
              {`${p.description} · ${p.qty} short on ${p.dispatchRef} ${p.shortCode} · ${p.reason.toLowerCase()}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
